import React, { useState, useEffect } from 'react';
import { Server, Activity, RefreshCw, Power, Pause, Moon, StopCircle, Copy, Eye, EyeOff, Info, Settings } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACSServerMonitoring = () => {
  const [acsInfo, setAcsInfo] = useState({
    url: '',
    username: '',
    password: '',
    uptime_seconds: 0
  });
  const [showPassword, setShowPassword] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchACSInfo();
  }, []);

  const fetchACSInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/acs/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setAcsInfo(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching ACS info:', error);
    }
  };

  const handleACSAction = async (action) => {
    const actionNames = {
      reboot: 'Reboot',
      pause: 'Pause',
      hibernate: 'Hibernate',
      stop: 'Stop'
    };

    const confirmed = window.confirm(
      `Apakah Anda yakin ingin ${actionNames[action]} ACS Server?\n\nIni akan mempengaruhi semua device yang terhubung!`
    );

    if (!confirmed) return;

    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/acs/control/${action}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(`ACS Server ${actionNames[action]} berhasil`);
      }
    } catch (error) {
      console.error(`Error ${action} ACS:`, error);
      toast.error(error.response?.data?.detail || `Gagal ${actionNames[action]} ACS Server`);
    } finally {
      setActionLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    // Try modern clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        toast.success(`${label} berhasil disalin`);
      }).catch((err) => {
        console.error('Clipboard API failed:', err);
        // Fallback to old method
        fallbackCopyToClipboard(text, label);
      });
    } else {
      // Use fallback method for non-HTTPS or older browsers
      fallbackCopyToClipboard(text, label);
    }
  };

  const fallbackCopyToClipboard = (text, label) => {
    try {
      // Create temporary textarea
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      // Try to copy
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        toast.success(`${label} berhasil disalin`);
      } else {
        toast.error('Gagal menyalin');
      }
    } catch (err) {
      console.error('Fallback copy failed:', err);
      toast.error('Gagal menyalin');
    }
  };

  const formatUptime = (seconds) => {
    if (!seconds) return 'N/A';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${days}d ${hours}h ${minutes}m`;
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-4 space-y-4">
      {/* Header - Compact */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center space-x-2">
              <Server className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
              <span>ACS Server Monitoring</span>
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              GenieACS TR-069 Management Console
            </p>
          </div>
          {acsInfo.uptime_seconds > 0 && (
            <div className="px-3 py-1.5 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-center space-x-2">
                <Activity className="w-4 h-4 text-green-600 dark:text-green-400 animate-pulse" />
                <div>
                  <div className="text-xs text-slate-600 dark:text-slate-400">Uptime</div>
                  <div className="text-sm font-semibold text-green-700 dark:text-green-300">{formatUptime(acsInfo.uptime_seconds)}</div>
                </div>
              </div>
            </div>
          )}
        </div>
        <Button
          onClick={fetchACSInfo}
          size="sm"
          className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
        >
          <RefreshCw className="w-3 h-3 mr-1.5" />
          Refresh
        </Button>
      </div>

      {/* ACS Server Information & Controls - Compact */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* ACS Server Info */}
        <Card className="border border-slate-200 dark:border-slate-700">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center space-x-2 text-base">
              <Info className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              <span>Server Information</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Server URL</label>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => copyToClipboard(acsInfo.url, 'URL')}
                  className="h-6 px-2"
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
              <Input
                value={acsInfo.url || 'Loading...'}
                readOnly
                className="h-8 bg-slate-50 dark:bg-slate-900 font-mono text-xs border-slate-200 dark:border-slate-700"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Username</label>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => copyToClipboard(acsInfo.username, 'Username')}
                  className="h-6 px-2"
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
              <Input
                value={acsInfo.username || 'Loading...'}
                readOnly
                className="h-8 bg-slate-50 dark:bg-slate-900 font-mono text-xs border-slate-200 dark:border-slate-700"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Password</label>
                <div className="flex items-center space-x-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowPassword(!showPassword)}
                    className="h-6 px-2"
                  >
                    {showPassword ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(acsInfo.password, 'Password')}
                    className="h-6 px-2"
                  >
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
              </div>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={acsInfo.password || 'Loading...'}
                readOnly
                className="h-8 bg-slate-50 dark:bg-slate-900 font-mono text-xs border-slate-200 dark:border-slate-700"
              />
            </div>

            <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                💡 Bagikan kredensial ini ke tenant untuk akses ACS
              </p>
            </div>
          </CardContent>
        </Card>

        {/* ACS Server Controls */}
        <Card className="border border-slate-200 dark:border-slate-700">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center space-x-2 text-base">
              <Settings className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              <span>Server Control</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* ACS Console Link */}
            <a
              href="http://acs.kumara-hotspot.net:3000"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between p-3 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg hover:shadow-md transition-all group"
            >
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-indigo-100 dark:bg-indigo-900/50 rounded-lg">
                  <Server className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-800 dark:text-white">ACS Console</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">acs.kumara-hotspot.net:3000</div>
                </div>
              </div>
              <div className="text-indigo-600 dark:text-indigo-400 group-hover:translate-x-1 transition-transform">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </div>
            </a>

            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={() => handleACSAction('reboot')}
                disabled={actionLoading}
                size="sm"
                className="bg-orange-500 hover:bg-orange-600 text-white h-9"
              >
                <Power className="w-3 h-3 mr-1.5" />
                Reboot
              </Button>

              <Button
                onClick={() => handleACSAction('pause')}
                disabled={actionLoading}
                size="sm"
                className="bg-yellow-500 hover:bg-yellow-600 text-white h-9"
              >
                <Pause className="w-3 h-3 mr-1.5" />
                Pause
              </Button>

              <Button
                onClick={() => handleACSAction('hibernate')}
                disabled={actionLoading}
                size="sm"
                className="bg-blue-500 hover:bg-blue-600 text-white h-9"
              >
                <Moon className="w-3 h-3 mr-1.5" />
                Hibernate
              </Button>

              <Button
                onClick={() => handleACSAction('stop')}
                disabled={actionLoading}
                size="sm"
                className="bg-red-500 hover:bg-red-600 text-white h-9"
              >
                <StopCircle className="w-3 h-3 mr-1.5" />
                Stop
              </Button>
            </div>

            <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
              <p className="text-xs text-red-500 dark:text-red-400">
                ⚠️ Actions affect all connected devices
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ACSServerMonitoring;
